/**
 * GUNNERY: the mode with targets in it, and the only mode that arms the aeroplane.
 *
 * ## Free flight is not a mode with the weapons switched off
 *
 * It is the *absence* of this object. `main.ts` constructs a `GunneryMode` when the player picks
 * gunnery and does not when they do not, and the master arm is set from `active` — so in free
 * flight there is nothing to arm, nothing to shoot at, and nothing to draw. That is why the
 * "free flight has zero weapons UI" requirement needed no UI work: the HUD reads the weapon
 * system, the weapon system is cold, and cold draws nothing.
 *
 * ## Two courses, one mode
 *
 * **Drones** — a slow drone flying a racetrack, and a second that jinks and breaks when it is
 * threatened. Scored on kills and accuracy.
 *
 * **Balloons** — six tethered balloons strung down a valley, zig-zagged so the run cannot be flown
 * in a straight line, and invisible to a seeker so it has to be flown with the gun. Scored on the
 * clock, which starts on the first hit.
 *
 * ## Where the course is
 *
 * Anchored to a latitude and longitude, and everything in it is an offset from that anchor — see
 * `targets.ts` for why that is what makes it survive a floating-origin rebase. The anchor is
 * converted to sim-frame NED once a tick, which is one geodetic conversion for the whole field.
 *
 * `updateFixed(dt)`: yes — {@link GunneryMode.updateFixed}. Allocation-free after setup; the
 * geodetic conversion writes into a preallocated vector.
 */

import { Vector3 } from 'three';
import { DEG2RAD, clamp } from '../../core/math.js';
import type { Rng } from '../../core/rng.js';
import type { StorageService } from '../../core/storage.js';
import type { HitQueue } from '../../aircraft/weapons/engagement.js';
import type { WeaponSystem } from '../../aircraft/weapons/weaponSystem.js';
import { TargetField, makeThreatInput, type ThreatInput } from '../targets.js';
import {
  RecordKeeper,
  makeScoreState,
  resetScore,
  scoreOf,
  type CourseId,
  type ScoreState,
} from '../scoring.js';

/** Which exercise is set up. */
export type { CourseId };

/** A place to fly a course, and the course to fly there. */
export interface CoursePlacement {
  latitudeDeg: number;
  longitudeDeg: number;
  /** Ground elevation at the anchor [m AMSL]. Everything is stacked on top of it. */
  groundElevationM: number;
}

/**
 * A canyon worth flying at a hundred feet.
 *
 * The Grand Canyon's south rim, a few miles north of the national park airport. Chosen because
 * the relief is genuinely there in the free elevation data — a "canyon course" over the gentle
 * terrain near a convenient airport would be a straight line with balloons on it, and the whole
 * point of the exercise is that the ground is in the way.
 */
export const CANYON_COURSE: CoursePlacement = {
  latitudeDeg: 36.0958,
  longitudeDeg: -112.0952,
  groundElevationM: 2_100,
};

/** True heading of the balloon line [deg]. The scripted canyon run is flown down it. */
export const BALLOON_COURSE_HEADING_DEG = 15;

/** How far apart the balloons are strung [m]. */
const BALLOON_SPACING_M = 700;
/** How far each balloon is offset to alternating sides [m]. */
const BALLOON_WEAVE_M = 220;
/** How high the balloons ride above the anchor's ground [m]. */
const BALLOON_HEIGHT_M = 120;
export const BALLOON_COUNT = 6;

export interface GunneryOptions {
  rng: Rng;
  storage: StorageService;
  /**
   * Sim-frame NED of a geodetic point. Supplied by the caller rather than imported, so this mode
   * never has to know about `WorldSystem` — which is also what lets the tests drive it.
   */
  geodeticToNed: (latitudeDeg: number, longitudeDeg: number, altitudeM: number, out: Vector3) => Vector3;
}

export class GunneryMode {
  readonly targets: TargetField;
  readonly score: ScoreState = makeScoreState();
  readonly records: RecordKeeper;

  /** Set for one tick when something died, so the effects and the pill can react. */
  killedThisTick = 0;
  /** Set for one tick when the course was completed. */
  completedThisTick = false;

  private readonly options: GunneryOptions;
  private readonly threat: ThreatInput = makeThreatInput();
  private readonly anchorNed = new Vector3();
  private placement: CoursePlacement = CANYON_COURSE;
  private courseValue: CourseId = 'drones';
  private activeValue = false;
  private started = false;
  /** Rounds the gun had left last tick, so shots fired can be differenced. */
  private lastAmmunition = 0;
  private lastMissiles = 0;

  constructor(options: GunneryOptions) {
    this.options = options;
    this.targets = new TargetField(options.rng);
    this.records = new RecordKeeper(options.storage);
    this.records.load();
  }

  get course(): CourseId {
    return this.courseValue;
  }

  get active(): boolean {
    return this.activeValue;
  }

  /** Where the course is, so the caller can offer to fly the player there. */
  get placementOf(): Readonly<CoursePlacement> {
    return this.placement;
  }

  /** Targets still alive. */
  get remaining(): number {
    return this.targets.aliveCount;
  }

  /**
   * Set up a course and arm the aircraft.
   *
   * The drone course is placed on the aircraft's own position so it can be started anywhere; the
   * balloon course has a fixed home, because it is a course and a course is a place.
   */
  begin(course: CourseId, placement: CoursePlacement, weapons: WeaponSystem): void {
    this.courseValue = course;
    this.placement = placement;
    this.activeValue = true;
    this.started = false;
    this.killedThisTick = 0;
    this.completedThisTick = false;
    resetScore(this.score);
    this.targets.clear();

    if (course === 'drones') this.buildDroneCourse();
    else this.buildBalloonCourse();

    weapons.reset();
    weapons.setArmed(true);
    this.lastAmmunition = weapons.cannon?.ammunition ?? 0;
    this.lastMissiles = weapons.missiles?.remaining ?? 0;
  }

  /** Leave the mode: targets gone, aircraft safe. */
  end(weapons: WeaponSystem): void {
    this.activeValue = false;
    this.targets.clear();
    weapons.setArmed(false);
    weapons.reset();
  }

  /** Restart the current course. */
  restart(weapons: WeaponSystem): void {
    this.begin(this.courseValue, this.placement, weapons);
  }

  /**
   * One physics step.
   *
   * Order: anchor the field, work out what is threatening the drones, move them, then drain the
   * weapons' hit queue. Draining last is what makes a hit this tick score against the position
   * the target actually occupied when the round arrived.
   */
  updateFixed(dt: number, weapons: WeaponSystem, hits: HitQueue): void {
    if (!this.activeValue) return;

    this.killedThisTick = 0;
    this.completedThisTick = false;

    // --- where the course is, this tick ---------------------------------------------------
    this.options.geodeticToNed(
      this.placement.latitudeDeg,
      this.placement.longitudeDeg,
      this.placement.groundElevationM,
      this.anchorNed,
    );
    this.targets.setAnchor(this.anchorNed.x, this.anchorNed.y, this.anchorNed.z);

    this.readThreat(weapons);
    this.targets.updateFixed(dt, this.threat);
    this.countShots(weapons);
    this.applyHits(hits);

    if (this.started && !this.score.complete) this.score.elapsedS += dt;

    if (this.score.kills > 0 && this.targets.aliveCount === 0 && !this.score.complete) {
      this.score.complete = true;
      this.completedThisTick = true;
      this.records.submit(this.courseValue, this.score);
    }
  }

  /** The course anchor in sim-frame NED, refreshed each tick. The scripted run rails to it. */
  get anchor(): Vector3 {
    return this.anchorNed;
  }

  /** The current score, for the HUD. */
  get points(): number {
    return scoreOf(this.score);
  }

  /** Tell the drones what is after them. */
  private readThreat(weapons: WeaponSystem): void {
    const threat = this.threat;
    const seeker = weapons.seeker?.state;
    threat.locked = seeker !== undefined && seeker.status === 'locked';
    threat.lockedTargetId = seeker?.targetId ?? -1;

    threat.missileInFlight = false;
    threat.missileTargetId = -1;
    const battery = weapons.missiles;
    if (battery === null) return;
    /*
     * The nearest guiding missile wins.
     *
     * A drone can only break one way, so with two in the air it must pick, and the one about to
     * arrive is the one worth defeating. Picking the first in the array instead would make a
     * second shot *safer* for the target than a first, which is nonsense.
     */
    let closest = Number.POSITIVE_INFINITY;
    for (const round of battery.rounds) {
      if (!round.active || !round.guiding) continue;
      for (const target of this.targets.all) {
        if (target.id !== round.targetId || !target.alive) continue;
        const range = round.positionNed.distanceTo(target.positionNed);
        if (range >= closest) continue;
        closest = range;
        threat.missileInFlight = true;
        threat.missileTargetId = round.targetId;
        threat.missilePositionNed.copy(round.positionNed);
        threat.missileVelocityNed.copy(round.velocityNed);
      }
    }
  }

  /** Difference the stores counts to learn what was fired, without the weapons reporting it. */
  private countShots(weapons: WeaponSystem): void {
    const ammunition = weapons.cannon?.ammunition ?? 0;
    if (ammunition < this.lastAmmunition) this.score.cannonFired += this.lastAmmunition - ammunition;
    this.lastAmmunition = ammunition;

    const missiles = weapons.missiles?.remaining ?? 0;
    if (missiles < this.lastMissiles) this.score.missilesFired += this.lastMissiles - missiles;
    this.lastMissiles = missiles;
  }

  /** Turn the tick's impacts into damage, kills and score. */
  private applyHits(hits: HitQueue): void {
    for (let i = 0; i < hits.length; i++) {
      const report = hits.at(i);
      if (report.kind === 'cannon') this.score.cannonHits++;
      else this.score.missileHits++;

      // The clock starts on the first thing you actually connect with.
      if (!this.started) this.started = true;

      if (this.targets.damage(report.targetId, report.damage)) {
        this.score.kills++;
        this.killedThisTick++;
      }
    }
  }

  private buildDroneCourse(): void {
    /*
     * Two drones on separate circuits at separate heights, so a pass at one does not accidentally
     * fly through the other. The slow one is the gunnery target the acceptance criterion names;
     * the fast one is the missile target, and it is the one that fights back.
     */
    this.targets.addDrone({
      kind: 'racetrack',
      hitPoints: 90,
      radiusM: 4,
      infraredIntensity: 0.75,
      jinkG: 2,
      breakG: 3,
      track: {
        centreNed: [0, 0, -(BALLOON_HEIGHT_M + 900)],
        straightM: 4_000,
        turnRadiusM: 1_200,
        speedMps: 95,
        headingDeg: 90,
      },
    });

    this.targets.addDrone({
      kind: 'evasive',
      hitPoints: 120,
      radiusM: 4,
      infraredIntensity: 1,
      jinkG: 5,
      /*
       * Fifteen g, which no aeroplane with a pilot in it could survive — and this one has no
       * pilot in it. `missile.test.ts` measures why the number has to be this high: pro-nav with
       * N = 4 needs about twice the target's g to null the miss it develops, so anything the
       * missile can simply out-pull is not an evasion at all. Nine g is not a manoeuvre, it is a
       * slightly delayed hit.
       */
      breakG: 15,
      track: {
        centreNed: [1_500, 2_000, -(BALLOON_HEIGHT_M + 1_500)],
        straightM: 5_000,
        turnRadiusM: 1_600,
        speedMps: 175,
        headingDeg: 20,
      },
    });
  }

  private buildBalloonCourse(): void {
    /*
     * Six balloons down the valley, weaving either side of the centre line.
     *
     * The weave is the exercise. Strung in a straight line they could be taken with one long
     * burst from four miles out; offset by a couple of hundred metres each way, the run has to be
     * flown — roll, pull, shoot, reverse — which at 500 kt and a hundred feet is the thing the
     * acceptance criterion is actually measuring the frame rate of.
     */
    const heading = BALLOON_COURSE_HEADING_DEG * DEG2RAD;
    const cos = Math.cos(heading);
    const sin = Math.sin(heading);
    for (let i = 0; i < BALLOON_COUNT; i++) {
      const along = (i - (BALLOON_COUNT - 1) / 2) * BALLOON_SPACING_M;
      const across = (i % 2 === 0 ? 1 : -1) * BALLOON_WEAVE_M;
      this.targets.addBalloon({
        positionNed: [
          along * cos - across * sin,
          along * sin + across * cos,
          // Local NED is down-positive and the anchor is already at ground level.
          -clamp(BALLOON_HEIGHT_M + (i % 3) * 25, 40, 400),
        ],
        // Two or three rounds. A balloon is a balloon.
        hitPoints: 24,
        radiusM: 3.5,
        bobAmplitudeM: 4,
        bobPeriodS: 7,
      });
    }
  }
}
